import { fold } from './i18n';

/**
 * Keys: the machine-readable half of a thing whose other half is a name.
 *
 * <p>A role key travels in the token and lands in the log, so it is ASCII, lower case and
 * hyphenated — a Turkish letter is a different sequence of bytes on two machines, and a
 * space breaks whatever reads the line apart. The server refuses anything else.</p>
 */

/** The letters a key may hold. Everything else becomes a hyphen. */
const KEY_SHAPE = /[^a-z0-9]+/g;

/**
 * Folds a name down to the alphabet a key is allowed.
 *
 * <p>Uses the same folding the search box does, so "Bölge Müdürü" and a search for
 * "bolge mudur" agree about which letters are which. The two letters handled after it are
 * the ones folding leaves alone.</p>
 */
function latin(text: string): string {
  return fold(text).replace(/ş/g, 's').replace(/ğ/g, 'g');
}

/**
 * A key suggested from a name, for the field that follows along as somebody types the name.
 *
 * <p>Trimmed at both ends, because a name is finished before this runs — it is read whole,
 * not keystroke by keystroke.</p>
 */
export function suggestKey(name: string): string {
  return latin(name).replace(KEY_SHAPE, '-').replace(/^-+|-+$/g, '').slice(0, 50);
}

/**
 * The same, applied to the key field as it is typed.
 *
 * <p><b>Does not trim the ends, and that is the whole difference.</b> Somebody typing
 * "bolge-muduru" has, for one keystroke, typed "bolge-" — strip the trailing hyphen there
 * and the next letter lands against the previous word, so the field quietly refuses to let
 * anybody type a hyphen at all. The ends are tidied when the name supplies the key and by
 * the server when it is saved.</p>
 */
export function cleanKey(typed: string): string {
  return latin(typed).replace(KEY_SHAPE, '-').slice(0, 50);
}
